import { useDeckBinding } from './DeckDataContext';
// Deck tabs support switching, inline renaming, confirmed deletion, and drag reordering.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { View, ScrollView, StyleSheet, Pressable, Platform, TextInput } from 'react-native';
import { useRouter } from 'expo-router';
import { useTheme } from '@/context/ThemeContext';

import { useModalHelper } from '@/hooks/useModalHelper';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { Tooltip } from '@/components/Tooltip';
import * as DropdownMenu from '@/components/DropdownMenu/DropdownMenu';
import DropdownMenuItem from '@/components/DropdownMenu/DropdownMenuItem';
import DeckAddColumnButton from './DeckAddColumnButton';

const TAB_HEIGHT = 64;
const EMPTY_TABS = [];

export default function DeckTabBar({ onAddColumn, columnCount }) {
  const { store: useDeckStore, tabsBefore = EMPTY_TABS, tabsAfter = EMPTY_TABS } = useDeckBinding();
  const { colors } = useTheme();
  const router = useRouter();

  const decks = useDeckStore(state => state.decks);
  const activeSlug = useDeckStore(state => state.activeDeckSlug);
  const limits = useDeckStore(state => state.limits);
  const { setActiveDeckSlug, createDeck, renameDeck, deleteDeck, reorderDecks } = useDeckStore(state => state.actions);
  const tabs = [...tabsBefore, ...decks, ...tabsAfter];

  const { useConfirmationModal } = useModalHelper();
  const showConfirmationModal = useConfirmationModal();

  const [renamingSlug, setRenamingSlug] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [dragging, setDragging] = useState({ slug: null, dropIndex: -1 });
  const tabRefs = useRef({});
  const dragCleanupRef = useRef(null);
  const creatingRef = useRef(false);
  const [isCreating, setIsCreating] = useState(false);
  useEffect(() => () => { dragCleanupRef.current?.(); }, []);

  const canAddDeck = decks.length < (limits?.maxDecks || 12);

  // ── Switching tabs ────────────────────────────────────────────────────
  const handleSelect = useCallback((slug) => {
    const external = [...tabsBefore, ...tabsAfter].find(tab => tab.slug === slug);
    if (external) { external.open(); return; }
    if (slug === activeSlug) return;
    setActiveDeckSlug(slug);
    // Mirror the active deck into the URL so refresh / share works.
    if (Platform.OS === 'web') {
      try {
        router.setParams({ d: slug });
      } catch (_) { /* router not ready, no-op */ }
    }
  }, [activeSlug, setActiveDeckSlug, router, tabsBefore, tabsAfter]);

  // ── Rename ────────────────────────────────────────────────────────────
  const startRename = useCallback((deck) => {
    if (deck.open) return;
    setRenamingSlug(deck.slug);
    setRenameValue(deck.name || '');
  }, []);

  const commitRename = useCallback(() => {
    if (!renamingSlug) return;
    const trimmed = renameValue.trim();
    const original = decks.find(d => d.slug === renamingSlug);
    if (trimmed && original && trimmed !== original.name) {
      renameDeck(renamingSlug, trimmed);
    }
    setRenamingSlug(null);
    setRenameValue('');
  }, [renamingSlug, renameValue, decks, renameDeck]);

  const cancelRename = useCallback(() => {
    setRenamingSlug(null);
    setRenameValue('');
  }, []);

  // ── Delete ────────────────────────────────────────────────────────────
  const handleDelete = useCallback((deck) => {
    showConfirmationModal({
      title: 'Delete deck?',
      message: `"${deck.name}" and its column layout will be removed. Existing conversations are not deleted.`,
      itemName: deck.name,
      confirmText: 'Delete deck',
      cancelText: 'Cancel',
      isDangerous: true,
      onConfirm: () => {
        deleteDeck(deck.slug);
      },
    });
  }, [deleteDeck, showConfirmationModal]);

  // ── New deck ──────────────────────────────────────────────────────────
  const handleAddDeck = useCallback(async () => {
    if (!canAddDeck || creatingRef.current) return;
    creatingRef.current = true;
    setIsCreating(true);
    const slug = await createDeck({ name: 'Untitled deck' });
    creatingRef.current = false;
    setIsCreating(false);
    if (!slug) return;
    if (Platform.OS === 'web') {
      try {
        router.setParams({ d: slug });
      } catch (_) {
        /* router not ready */
      }
    }
  }, [canAddDeck, createDeck, router]);

  // ── Drag-to-reorder (web only, mouse-based) ───────────────────────────
  const handleTabDragStart = useCallback((slug, e) => {
    if (Platform.OS !== 'web') return;
    if (renamingSlug) return;
    dragCleanupRef.current?.();

    const startX = e.clientX;
    let hasMoved = false;

    const fromIndex = decks.findIndex(d => d.slug === slug);
    if (fromIndex === -1) return;

    const computeDropIndex = (mouseX) => {
      for (let i = 0; i < decks.length; i++) {
        const el = tabRefs.current[decks[i].slug];
        if (!el) continue;
        const rect = el.getBoundingClientRect();
        const midX = rect.left + rect.width / 2;
        if (mouseX < midX) return i;
      }
      return decks.length;
    };

    let latestDropIndex = -1;

    const onMove = (ev) => {
      if (!hasMoved && Math.abs(ev.clientX - startX) < 6) return;
      hasMoved = true;
      document.body.style.cursor = 'grabbing';
      document.body.style.userSelect = 'none';
      const di = computeDropIndex(ev.clientX);
      latestDropIndex = di;
      setDragging({ slug, dropIndex: di });
    };

    const cleanup = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      dragCleanupRef.current = null;
    };
    const onUp = () => {
      cleanup();

      if (hasMoved && latestDropIndex !== -1) {
        let toIndex = latestDropIndex;
        if (toIndex > fromIndex) toIndex -= 1;
        if (toIndex !== fromIndex) reorderDecks(fromIndex, toIndex);
      }
      setDragging({ slug: null, dropIndex: -1 });
    };

    dragCleanupRef.current = cleanup;
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [decks, renamingSlug, reorderDecks]);

  // ── Render ────────────────────────────────────────────────────────────
  const isDragging = dragging.slug !== null;
  const dragFromIndex = isDragging ? decks.findIndex(d => d.slug === dragging.slug) : -1;

  if (!tabs.length) return null;

  return (
    <View
      style={[
        styles.bar,
        {
          backgroundColor: colors.solid_color_secondary,
        },
      ]}
    >
      <ScrollView
        horizontal
        accessibilityRole="tablist"
        showsHorizontalScrollIndicator={false}
        style={styles.tabScroll}
        contentContainerStyle={styles.scroll}
      >
        {tabs.map((deck, index) => {
          const active = !deck.open && deck.slug === activeSlug;
          const ownedIndex = decks.findIndex(owned => owned.slug === deck.slug);
          const beingDragged = dragging.slug === deck.slug;
          const showDropBefore =
            isDragging
            && ownedIndex >= 0
            && dragging.dropIndex === ownedIndex
            && dragFromIndex !== ownedIndex
            && dragFromIndex !== ownedIndex - 1;
          const showDropAfter =
            isDragging
            && dragging.dropIndex === decks.length
            && ownedIndex === decks.length - 1
            && dragFromIndex !== ownedIndex;

          const isRenamingThis = renamingSlug === deck.slug;

          return (
            <React.Fragment key={deck.slug}>
              {showDropBefore && <View style={[styles.dropIndicator, { backgroundColor: colors.text_color_primary }]} />}
              <Pressable
                ref={(node) => {
                  if (node) tabRefs.current[deck.slug] = node;
                  else delete tabRefs.current[deck.slug];
                }}
                onPress={() => handleSelect(deck.slug)}
                accessibilityRole="tab"
                accessibilityLabel={`${deck.name || 'Untitled'} deck`}
                accessibilityState={{ selected: active }}
                onLongPress={() => startRename(deck)}
                {...(Platform.OS === 'web'
                  ? {
                      tabIndex: active ? 0 : -1,
                      onMouseDown: (e) => {
                        // Avoid hijacking right-clicks and modifier-clicks.
                        if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
                        if (isRenamingThis) return;
                        if (!deck.open) handleTabDragStart(deck.slug, e);
                      },
                      onDoubleClick: () => startRename(deck),
                      onKeyDown: (e) => {
                        if (e.target !== e.currentTarget || isRenamingThis) return;
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleSelect(deck.slug);
                        }
                        const direction = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
                        const nextIndex = e.key === 'Home' ? 0 : e.key === 'End' ? tabs.length - 1
                          : direction ? (index + direction + tabs.length) % tabs.length : null;
                        if (nextIndex !== null) {
                          e.preventDefault();
                          const nextSlug = tabs[nextIndex].slug;
                          handleSelect(nextSlug);
                          tabRefs.current[nextSlug]?.focus();
                        }
                      },
                    }
                  : {})}
                style={({ hovered }) => [
                  styles.tab,
                  {
                    backgroundColor: active
                      ? colors.solid_color_primary
                      : hovered ? colors.solid_color_tertiary : 'transparent',
                    borderColor: active ? colors.border_color_secondary : 'transparent',
                    opacity: beingDragged ? 0.5 : 1,
                  },
                  active && styles.tabActive,
                ]}
              >
                <Icon name="table-columns" size={14} color={active ? colors.text_color_primary : colors.text_color_secondary} />

                {isRenamingThis ? (
                  <TextInput
                    autoFocus
                    value={renameValue}
                    onChangeText={setRenameValue}
                    onBlur={commitRename}
                    onSubmitEditing={commitRename}
                    onKeyPress={(e) => {
                      if (e?.nativeEvent?.key === 'Escape') cancelRename();
                    }}
                    selectTextOnFocus={Platform.OS !== 'macos'}
                    style={[styles.renameInput, { color: colors.text_color_primary }]}
                    maxLength={60}
                  />
                ) : (
                  <Text
                    size="xs"
                    weight={active ? 'semibold' : 'medium'}
                    color={active ? 'primary' : 'secondary'}
                    numberOfLines={1}
                    style={styles.tabLabel}
                  >
                    {deck.name || 'Untitled deck'}
                  </Text>
                )}

                {active && !isRenamingThis && (
                  <View onMouseDown={e => e.stopPropagation()}>
                    <DropdownMenu.Root>
                      <DropdownMenu.Trigger asChild style={styles.menuBtn}>
                        <Pressable accessibilityRole="button" accessibilityLabel="Deck options" style={styles.menuBtn}>
                          <Icon name="ellipsis" size={14} color={colors.text_color_secondary} />
                        </Pressable>
                      </DropdownMenu.Trigger>
                      <DropdownMenu.Content className="DropdownMenuContent" align="start" sideOffset={8}>
                        <DropdownMenuItem title="Rename deck" icon="pencil" iosIcon="pencil" onSelect={() => startRename(deck)} />
                        <DropdownMenuItem title="Delete deck" icon="trash" iosIcon="trash" isDanger onSelect={() => handleDelete(deck)} />
                      </DropdownMenu.Content>
                    </DropdownMenu.Root>
                  </View>
                )}
              </Pressable>
              {showDropAfter && <View style={[styles.dropIndicator, { backgroundColor: colors.text_color_primary }]} />}
            </React.Fragment>
          );
        })}

      </ScrollView>

      <Tooltip label={canAddDeck ? 'New deck' : 'Deck limit reached'} side="bottom">
      <Pressable
        onPress={handleAddDeck}
        accessibilityRole="button"
        accessibilityLabel="New deck"
        disabled={!canAddDeck || isCreating}
        style={({ hovered }) => [
          styles.addBtn,
          hovered && { backgroundColor: colors.solid_color_tertiary },
          (!canAddDeck || isCreating) && { opacity: 0.4 },
        ]}
      >
        <Icon
          name="plus"
          size={13}
          color={colors.text_color_secondary}
        />
        <Text
          size="s"
          color="secondary"
          style={{ marginLeft: 6 }}
        >
          New deck
        </Text>
      </Pressable>
      </Tooltip>
      {decks.length > 0 && <DeckAddColumnButton onAddColumn={onAddColumn} columnCount={columnCount} />}
    </View>
  );
}

const styles = StyleSheet.create({
  bar: {
    height: TAB_HEIGHT,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    gap: 8,
    flexShrink: 0,
  },
  tabScroll: {
    flex: 1,
    minWidth: 0,
  },
  scroll: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flexGrow: 1,
  },
  tab: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 36,
    paddingHorizontal: 12,
    paddingRight: 8,
    borderRadius: 10,
    borderWidth: 1,
    gap: 8,
    maxWidth: 240,
    minWidth: 120,
    ...(Platform.OS === 'web' ? { cursor: 'grab', userSelect: 'none', transition: 'background-color 0.15s ease' } : {}),
  },
  tabActive: {
    ...(Platform.OS === 'web' ? { boxShadow: '0 1px 0 rgba(0,0,0,0.04)' } : {}),
  },
  tabLabel: {
    flex: 1,
    minWidth: 0,
  },
  renameInput: {
    flex: 1,
    fontSize: 13,
    fontWeight: '600',
    padding: 0,
    margin: 0,
    minWidth: 60,
    ...(Platform.OS === 'web' ? { outlineStyle: 'none' } : {}),
  },
  menuBtn: {
    width: 26,
    height: 26,
    borderRadius: 13,
    alignItems: 'center',
    justifyContent: 'center',
    ...(Platform.OS === 'web' ? { cursor: 'pointer' } : {}),
  },
  dropIndicator: {
    width: 2,
    height: 34,
    alignSelf: 'center',
    borderRadius: 1,
    marginHorizontal: 1,
  },
  addBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'center',
    height: 36,
    paddingHorizontal: 14,
    borderRadius: 10,
    flexShrink: 0,
    ...(Platform.OS === 'web' ? { cursor: 'pointer', transition: 'background-color 0.15s ease' } : {}),
  },
});
